import { useEffect, useState } from "react";
import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
  Box,
  Button,
  EmptyState,
  Icon,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  Text,
  toastManager,
} from "@salestrak/ui";
import { EntityTag } from "#app/components/entity";
import { useAuth } from "#app/lib/auth-context";
import { useDebouncedValue } from "#app/lib/hooks/use-debounced-value";
import { readJWT } from "#app/lib/jwt";
import { AdminUserWorkspacesDocument } from "#app/graphql/generated/graphql";
import { PanelCard, ScreenHeader } from "./admin-ui";
import { useAdminFetch } from "./admin-shared";
import type { WorkspaceRef } from "./use-admin-data";

type SearchResult = { userId: string; name: string; email: string };

function UserTag({ user }: { user: SearchResult }) {
  return (
    <EntityTag
      entity={{ kind: "user", id: user.userId, user: { name: user.name, email: user.email } }}
      subtitle
      interactive={false}
      size="sm"
    />
  );
}

export function ImpersonateScreen() {
  const { loginFetch, coreGql } = useAdminFetch();
  const { startImpersonation, state: authState } = useAuth();
  const selfId = authState.accessToken ? readJWT(authState.accessToken).userId : undefined;

  const [search, setSearch] = useState("");
  const debounced = useDebouncedValue(search, 300);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState<SearchResult | null>(null);
  const [workspaces, setWorkspaces] = useState<WorkspaceRef[]>([]);
  const [workspacesLoading, setWorkspacesLoading] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState<WorkspaceRef | null>(null);
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    if (debounced.trim().length < 2) {
      setResults([]);
      return;
    }
    let cancelled = false;
    setSearching(true);
    (async () => {
      try {
        const res = await loginFetch(`/oAuth/users/search?query=${encodeURIComponent(debounced)}`);
        const body = await res.json();
        if (!cancelled) setResults((body.users ?? []).filter((u: SearchResult) => u.userId !== selfId));
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [debounced, loginFetch, selfId]);

  useEffect(() => {
    if (!selected) {
      setWorkspaces([]);
      return;
    }
    let cancelled = false;
    setWorkspacesLoading(true);
    (async () => {
      try {
        const data = await coreGql(AdminUserWorkspacesDocument, { userIds: [selected.userId] });
        const row = (data.adminUserWorkspaces ?? []).find((r) => r.userId === selected.userId);
        if (!cancelled) setWorkspaces(row?.workspaces.map((w) => ({ id: w.id, name: w.name, urlPath: w.urlPath })) ?? []);
      } catch {
        if (!cancelled) setWorkspaces([]);
      } finally {
        if (!cancelled) setWorkspacesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selected, coreGql]);

  const start = async (workspace: WorkspaceRef) => {
    if (!selected) return;
    setStarting(true);
    try {
      await startImpersonation({ userId: selected.userId, workspace });
    } catch (err) {
      setStarting(false);
      toastManager.add({
        title: "Failed to start impersonation",
        description: err instanceof Error ? err.message : undefined,
        type: "error",
      });
    }
  };

  const displayName = selected?.name || selected?.email || "this user";

  return (
    <Box fill="flex" overflow="auto">
      <ScreenHeader title="Impersonate" meta="Act as a member inside one of their workspaces for up to an hour" />

      <Box paddingX="lg" paddingBottom="xl" paddingTop="sm" gap="md">
        {selected ? (
          <PanelCard>
            <Box direction="row" align="center" gap="sm" padding="sm">
              <Box fill="flex" minWidth={0}>
                <UserTag user={selected} />
              </Box>
              <Button variant="ghost" size="sm" onClick={() => setSelected(null)}>
                Change
              </Button>
            </Box>
            <Box border="top" padding="sm" gap="2xs">
              <Text size="xs" variant="muted" weight="semibold">
                Workspaces
              </Text>
              {workspacesLoading ? (
                <Text size="sm" variant="muted">
                  Loading…
                </Text>
              ) : null}
              {!workspacesLoading && workspaces.length === 0 ? (
                <EmptyState size="xs" title="Not an active member of any workspace" />
              ) : null}
              {workspaces.map((w) => (
                <Box key={w.id} direction="row" align="center" gap="sm" paddingY="2xs">
                  <Icon name="layout-grid" size="sm" color="muted" />
                  <Box fill="flex" minWidth={0}>
                    <Text size="sm" truncate={1}>
                      {w.name}
                    </Text>
                  </Box>
                  <Button variant="outline" size="sm" onClick={() => setConfirmTarget(w)}>
                    Impersonate
                  </Button>
                </Box>
              ))}
            </Box>
          </PanelCard>
        ) : (
          <PanelCard>
            <Box padding="sm">
              <InputGroup>
                <InputGroupAddon>
                  <Icon name="search" />
                </InputGroupAddon>
                <InputGroupInput
                  type="search"
                  value={search}
                  onValueChange={setSearch}
                  placeholder="Search users by name or email…"
                />
              </InputGroup>
            </Box>
            <Box paddingX="sm" paddingBottom="sm" gap="2xs">
              {searching ? (
                <Text size="sm" variant="muted">
                  Searching…
                </Text>
              ) : null}
              {!searching && debounced.trim().length >= 2 && results.length === 0 ? (
                <Text size="sm" variant="muted">
                  No users found
                </Text>
              ) : null}
              {results.map((r) => (
                <Box key={r.userId} direction="row" align="center" gap="sm" paddingY="2xs">
                  <Box fill="flex" minWidth={0}>
                    <UserTag user={r} />
                  </Box>
                  <Button variant="outline" size="sm" onClick={() => setSelected(r)}>
                    Select
                  </Button>
                </Box>
              ))}
            </Box>
          </PanelCard>
        )}

        <Box direction="row" align="center" gap="xs">
          <Icon name="info" size="xs" color="muted" />
          <Text size="xs" variant="muted">
            You act with the member&apos;s permissions. What you do stays theirs, but shows as SalesTrak Admin on
            their behalf.
          </Text>
        </Box>
      </Box>

      <AlertDialog open={!!confirmTarget} onOpenChange={(o) => !o && !starting && setConfirmTarget(null)}>
        <AlertDialogPopup>
          <AlertDialogHeader>
            <AlertDialogTitle>Impersonate {displayName}</AlertDialogTitle>
            <AlertDialogDescription>
              {`You will act as ${displayName} in ${confirmTarget?.name ?? "this workspace"} for up to an hour. The app reloads into their session; exit from the banner to return to your own.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogClose render={<Button variant="outline" disabled={starting} />}>Cancel</AlertDialogClose>
            <Button disabled={starting} onClick={() => confirmTarget && void start(confirmTarget)}>
              {starting ? "Starting…" : "Impersonate"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogPopup>
      </AlertDialog>
    </Box>
  );
}
